/**
 * The mission-planning track, and how the app knows each lesson was done.
 *
 * Lessons are planning, not flying. The predecessor's first track taught arm,
 * take off and land, which a trainee on the planning screen could not do and an
 * operator below tactical is not allowed to do anywhere. Every lesson here is
 * something an operator does on Planning.
 *
 * A lesson ticks itself when the app observes the work, never from a "mark as
 * read" box, and only against the trainee's own practice aircraft: a route
 * somebody uploads to a real aircraft in another tab is not a lesson done.
 * Everything below is a pure function of what the stores hold, so it is tested
 * without a map, a socket or a server.
 */

import type { CarrierPlan } from '@/api/carriers';
import type { Waypoint } from '@/types/domain';

export type LessonId = 'select' | 'route' | 'pattern' | 'zone' | 'target' | 'upload' | 'carrier';

export type LessonIcon = 'select' | 'route' | 'pattern' | 'zone' | 'target' | 'upload' | 'carrier';

export interface Lesson {
  id: LessonId;
  title: string;
  /** One line on what the trainee can do afterwards. */
  outcome: string;
  steps: string[];
  /** What the app is watching for, said to the trainee. */
  check: string;
  icon: LessonIcon;
}

export const LESSONS: readonly Lesson[] = [
  {
    id: 'select',
    icon: 'select',
    title: 'Pick an aircraft to plan for',
    outcome: 'Choose which aircraft a route belongs to.',
    steps: [
      'Every aircraft carries its own route. In the PLAN panel on the left, the SIM tab lists your practice aircraft: two quads and a boat, each named PRACTICE.',
      'Click a quad by name to plan for it. Whatever you draw next belongs to that quad, and the badge at the top of the panel says whether its route is a draft or already on the aircraft.',
      'Tick the boxes beside several aircraft, or ctrl-click their names, when one plan should go to more than one of them.',
    ],
    check: 'Checks itself when you select one of your practice aircraft.',
  },
  {
    id: 'route',
    icon: 'route',
    title: 'Draw a route',
    outcome: 'Lay down the waypoints an aircraft will fly.',
    steps: [
      'With a quad selected, click the map to drop waypoints in the order they should be flown. The palette at the bottom sets what the next click adds: WP for a waypoint, LOITER to hold, LAND to finish on the ground.',
      'Drag a waypoint to move it. Right-click the map for the same items, or to move the nearest waypoint to that spot, or to clear the draft.',
      'The panel on the right totals the distance and flight time leg by leg, and the profile along the bottom shows the altitude of every leg.',
    ],
    check: 'Checks itself once a practice aircraft has a route of two waypoints or more.',
  },
  {
    id: 'pattern',
    icon: 'pattern',
    title: 'Cover an area with a search pattern',
    outcome: 'Sweep a whole area evenly instead of placing every point by hand.',
    steps: [
      'Centre the map on the area to be searched. The pattern is laid out around the middle of the map.',
      'Under SEARCH PATTERN on the right, choose a shape, set the radius, the spacing between passes and the altitude, then press GENERATE.',
      'Select both quads first and the area is divided between them: each flies its own share, and no strip is flown twice.',
    ],
    check: 'Checks itself when you generate a pattern.',
  },
  {
    id: 'zone',
    icon: 'zone',
    title: 'Draw a keep-out zone, then remove it',
    outcome: 'Mark ground that no route may cross.',
    steps: [
      'Under DRAW A ZONE, pick a KEEP-OUT shape and click the map: the centre and then the edge for a circle, two opposite corners for a rectangle, or each corner in turn for a polygon.',
      'A route that crosses the zone is flagged, and UPLOAD is refused until you move the waypoints or press ROUTE AROUND.',
      'Zones are shared with everyone on this server, practice or not, and a goto across one is refused for real aircraft as well. When you have seen yours work, remove it with the × on its row in the zone list.',
    ],
    check: 'Checks itself once you have drawn a zone and removed it again.',
  },
  {
    id: 'target',
    icon: 'target',
    title: 'Mark a target',
    outcome: 'Put a point of interest on the map and see an approach to it.',
    steps: [
      'Open TARGETING at the top of the map, press PLACE TARGET, then click the map where the target is.',
      'Choose DIVE or DROP and press COMPUTE to see the approach for the selected aircraft drawn on the map. It only plans: nothing flies until a route is uploaded and started.',
      'Press MOVE TARGET to put it somewhere else. The old approach is cleared the moment the target moves, so the map never shows numbers for a point you have left.',
    ],
    check: 'Checks itself when a target is on the map.',
  },
  {
    id: 'upload',
    icon: 'upload',
    title: 'Send the route to the aircraft',
    outcome: 'Put the finished route on the aircraft itself.',
    steps: [
      'With a route drawn and no zone in its way, press UPLOAD in the PLAN panel and confirm in the dialog.',
      'The badge changes from DRAFT to IN SYNC once the aircraft acknowledges the route. A route the aircraft never confirmed is never shown as on board.',
      'Arming the aircraft and starting the route happen on the Command screen, by whoever commands it.',
    ],
    check: 'Checks itself when a practice aircraft confirms a route.',
  },
  {
    id: 'carrier',
    icon: 'carrier',
    title: 'Put drones aboard the boat',
    outcome: 'Have quads ride the boat and launch from a waypoint on its route.',
    steps: [
      'Select the practice boat, draw its route with a few waypoints, and upload it. Launch points are waypoints on the route the boat holds.',
      'Draw the route a quad should fly once it is in the air, then under CARRIER on the right choose that quad, the waypoint it launches at and its launch altitude, and press PUT ABOARD.',
      'Once the boat is under way and reaches that waypoint, the server arms each quad, climbs it clear of the deck and starts its route, with nobody needing a screen open. A quad that has landed is recovered to the boat from the same panel.',
    ],
    check: 'Checks itself once a quad is aboard your practice boat.',
  },
];

export interface LessonObservation {
  practiceIds: ReadonlySet<string>;
  selectedIds: readonly string[];
  drafts: Readonly<Record<string, readonly Waypoint[] | undefined>>;
  uploaded: Readonly<Record<string, readonly Waypoint[] | undefined>>;
  targetPlaced: boolean;
  patternGenerated: boolean;
  zoneDrawnAndRemoved: boolean;
  carrierPlans: readonly CarrierPlan[];
}

function positional(route: readonly Waypoint[] | undefined): number {
  if (!route) return 0;
  return route.filter((wp) => !(wp.lat === 0 && wp.lon === 0)).length;
}

/** Which lessons the current state shows done. Practice aircraft only. */
export function observedLessons(o: LessonObservation): Set<LessonId> {
  const done = new Set<LessonId>();
  const mine = (id: string) => o.practiceIds.has(id);
  if (o.practiceIds.size === 0) return done;

  if (o.selectedIds.some(mine)) done.add('select');
  if (Object.entries(o.drafts).some(([id, route]) => mine(id) && positional(route) >= 2)) {
    done.add('route');
  }
  if (o.patternGenerated) done.add('pattern');
  if (o.zoneDrawnAndRemoved) done.add('zone');
  if (o.targetPlaced) done.add('target');
  if (Object.entries(o.uploaded).some(([id, route]) => mine(id) && positional(route) >= 1)) {
    done.add('upload');
  }
  if (o.carrierPlans.some((plan) => mine(plan.carrierId) && plan.embarked.some((e) => mine(e.droneId)))) {
    done.add('carrier');
  }
  return done;
}

/**
 * The zone lesson's memory: which zones existed when practice began, and which
 * the trainee has drawn since.
 *
 * Drawing alone does not finish it. Zones are deployment-wide, so a practice
 * keep-out left behind refuses real gotos across it; the lesson is only done
 * once the trainee has removed what they drew.
 */
export interface ZoneTrack {
  baseline: ReadonlySet<string> | null;
  drawn: ReadonlySet<string>;
  done: boolean;
}

export const NEW_ZONE_TRACK: ZoneTrack = { baseline: null, drawn: new Set(), done: false };

export function trackZones(track: ZoneTrack, zoneIds: readonly string[]): ZoneTrack {
  const present = new Set(zoneIds);
  if (track.baseline === null) return { baseline: present, drawn: new Set(), done: track.done };
  const drawn = new Set(track.drawn);
  for (const id of present) if (!track.baseline.has(id)) drawn.add(id);
  const removedOne = [...drawn].some((id) => !present.has(id));
  const done = track.done || removedOne;
  const unchanged = drawn.size === track.drawn.size && done === track.done;
  return unchanged ? track : { baseline: track.baseline, drawn, done };
}
